import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Center,
  Heading,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  SkeletonText,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SEChargeHeaderIconDark, SEChargeHeaderIconLight } from 'clipsal-cortex-icons/src/custom-icons';
import { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';

import packageJson from '../../../../package.json';
import { useReduxDispatch } from '../../../app/store';
import { ContactSupportButton } from '../../../common/components/ContactSupportButton';
import { FadingText } from '../../../common/components/FadingText';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { BOTTOM_NAV_HEIGHT, IS_PRODUCTION, IS_PRODUCTION_DATA } from '../../../common/constants';
import { trackCablePluggedInWithoutSchedule, trackCablePluggedOut } from '../../../utils/common/analytics';
import { deriveGlobalUiMode, GlobalUiMode } from '../../../utils/common/deriveGlobalUiMode';
import { findNextScheduleOccurrence, formatScheduleTime } from '../../../utils/common/time-utils';
import {
  trackDynatraceCablePluggedInWithoutSchedule,
  trackDynatraceCablePluggedOut,
  trackDynatraceDeviceStatusOpened,
} from '../../../utils/native/dynatrace-events';
import { selectEvChargers, selectSite } from '../../site/siteSlice';
import { selectFirmwareUpdateStatus, setFirmwareUpdateStatus } from '../../smart-amp/evseStreamSlice';
import { useSmartAmpSse } from '../../smart-amp/useSmartAmpSse';
import { fetchEvChargerSchedules, selectEvChargerSchedules } from '../schedules/schedulerSlice';
import { ChargingControl } from './ChargingControl';
import { deriveEvseDeviceActive, selectEvChargerData, selectEvseType, setIsLoaded } from './evChargerSlice';
import { clearSeenFirmwareToasts, hasSeenFirmwareToast, markFirmwareToastSeen } from './firmwareToastStorage';
import { FirmwareUpdatedToast } from './FirmwareUpdatedToast';
import { MultipurposeBanner } from './MultipurposeBanner';
import { StatusAnimation } from './StatusAnimation';

// These statuses will not have description below charging status.
const NO_DESCRIPTION_STATUSES: ChargerStatus[] = ['Preparing'];
const COMMISSIONING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
// Max time a firmware update UI state can persist before falling back to normal dashboard.
const FIRMWARE_UPDATE_CHECK_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const FW_UPDATE_UI_MODES: GlobalUiMode[] = ['fw-pending', 'fw-downloading', 'fw-installing'];
const WEEKDAY_I18N_KEYS = [
  'Common.sun',
  'Common.mon',
  'Common.tue',
  'Common.wed',
  'Common.thu',
  'Common.fri',
  'Common.sat',
] as const;

/* istanbul ignore next -- @preserve */
export const DeviceStatus = () => {
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const evChargers = useSelector(selectEvChargers);
  const chargerId = evChargers[0]?.site_identifier ?? null;
  const site = useSelector(selectSite);
  const {
    siteDeviceId,
    isLoaded,
    status,
    vendorErrorCode,
    chargeSimulationType,
    control_mode: controlMode,
    is_scheduled_charge_active: isScheduledActive,
    scheduled_charge_start_time: scheduledStart,
    scheduled_charge_end_time: scheduledEnd,
    commissioningStartedAt,
  } = useSelector(selectEvChargerData);
  const evseType = useSelector(selectEvseType);
  const { loadActive: smartAmpLoadActive, tempActive: smartAmpTempActive, status: smartAmpStatus } = useSmartAmpSse();
  const { firmwareUpdateStatus, cloudFirmwareStatus, firmwareVersion } = useSelector(selectFirmwareUpdateStatus);
  const toast = useToast({ isClosable: true, position: 'top' });

  const HeaderIcon = useColorModeValue(SEChargeHeaderIconLight, SEChargeHeaderIconDark);
  const hasEvChargers = !!evChargers?.length;
  useEffect(() => {
    if (chargerId) trackDynatraceDeviceStatusOpened(chargerId);
  }, [chargerId]);
  const iconColor = useColorModeValue('customGrey.900', 'customGrey.400');

  const { isLoaded: areSchedulesLoaded, schedules } = useSelector(selectEvChargerSchedules);
  const activeSchedules = schedules.filter((schedule) => !schedule.isEditing);
  const isTimedScheduleEnabled = activeSchedules.length > 0;
  const firstActiveSchedule = activeSchedules[0] ?? null;
  const nextScheduleOccurrence = useMemo(
    () => findNextScheduleOccurrence(activeSchedules, site.timezone || 'UTC'),
    [activeSchedules, site.timezone]
  );
  const scheduleStartTime = useMemo(
    () => nextScheduleOccurrence?.schedule.startTime || firstActiveSchedule?.startTime || scheduledStart || null,
    [firstActiveSchedule?.startTime, nextScheduleOccurrence, scheduledStart]
  );
  const scheduleStartDayLabel =
    nextScheduleOccurrence != null
      ? t(WEEKDAY_I18N_KEYS[nextScheduleOccurrence.startDay]).replace(/^./, (value) => value.toUpperCase())
      : null;
  const previousStatus = useRef<string | null>(null);
  const [commissioningNow, setCommissioningNow] = useState(Date.now());
  // Commissioning times out after 5 minutes while site is PENDING and before first SSE status update.
  const isCommissioningTimedOut = useMemo(() => {
    const isPendingBeforeFirstSse = site.commissioning_status === 'PENDING' && !isLoaded;
    if (!isPendingBeforeFirstSse) return false;
    if (!commissioningStartedAt) return true;
    return commissioningNow - commissioningStartedAt > COMMISSIONING_TIMEOUT_MS;
  }, [site.commissioning_status, isLoaded, commissioningStartedAt, commissioningNow]);
  const [animationWidth, setAnimationWidth] = useState(
    window.innerHeight <= 700 ? 'min(100%, calc(35vh * 1.121))' : 'min(100%, calc(45vh * 1.121))'
  );
  const { isSmartAmpActive, isSmartPlugActive } = deriveEvseDeviceActive(
    evseType,
    chargeSimulationType,
    smartAmpLoadActive,
    smartAmpTempActive
  );
  const mode = deriveGlobalUiMode(
    status as ChargerStatus,
    isScheduledActive,
    isTimedScheduleEnabled,
    scheduledStart,
    scheduledEnd,
    isSmartAmpActive,
    isSmartPlugActive,
    cloudFirmwareStatus,
    firmwareUpdateStatus
  );
  const isFirmwareMode = FW_UPDATE_UI_MODES.includes(mode);
  // Firmware update mode takes precedence so firmware title/body are shown even if commissioning is still PENDING.
  const isCommissioning =
    site.commissioning_status === 'PENDING' && !isLoaded && !isCommissioningTimedOut && !isFirmwareMode;
  // Skeleton only shows while waiting for first SSE update unless commissioning or firmware mode is active.
  const isSkeletonBypassed = isLoaded || isCommissioning || isCommissioningTimedOut || isFirmwareMode;
  const animationStatus: ChargerStatus = isCommissioning
    ? 'Unavailable_Disconnect'
    : isFirmwareMode
    ? 'Available'
    : (status as ChargerStatus);
  const errorTitle = vendorErrorCode ? t(`EVSE.errorCodeKey.${vendorErrorCode}.title`, { defaultValue: '' }) : '';
  const errorMessage = vendorErrorCode ? t(`EVSE.errorCodeKey.${vendorErrorCode}.content`, { defaultValue: '' }) : '';
  const isSmartAmpLoadDeratingActive = smartAmpStatus === 'active' && smartAmpLoadActive;
  const isSmartAmpThermalDeratingActive = smartAmpStatus === 'active' && smartAmpTempActive;
  const isSmartAmpChargingLimited = smartAmpStatus === 'chargingLimited';
  const hasSmartAmpDeratingDetails = isSmartAmpLoadDeratingActive || isSmartAmpThermalDeratingActive;
  const hasSmartAmpDetails = hasSmartAmpDeratingDetails || isSmartAmpChargingLimited;
  const descriptionDetails = errorMessage
    ? errorMessage
    : status === 'SuspendedEV'
    ? t('EVSE.suspended ev details')
    : isSmartAmpLoadDeratingActive || (isSmartAmpChargingLimited && smartAmpLoadActive)
    ? t('EVSE.smartamp load details')
    : isSmartAmpThermalDeratingActive || (isSmartAmpChargingLimited && smartAmpTempActive)
    ? t('EVSE.smartamp thermal details')
    : '';
  const chargerStateTitle = () => {
    if (errorTitle) return errorTitle;
    if (hasSmartAmpDeratingDetails)
      return evseType === 'SmartAmp' ? t('EVSE.charging power limited') : t('EVSE.charging slowed');

    switch (mode) {
      case 'error':
        return errorTitle;
      case 'fw-pending':
        return t('EVSE.firmware update check title');
      case 'fw-downloading':
        return t('EVSE.downloading firmware title');
      case 'fw-installing':
        return t('EVSE.firmware is updating title');
      case 'evse-smartamp-stopped':
        return t('EVSE.evse smartamp stopped title');
      case 'evse-smartplug-stopped':
        return t('EVSE.evse smartplug temperature increased title');
      case 'scheduled-waiting':
        return t('EVSE.charging scheduled');
      case 'charging':
        return controlMode === 'Manual' ? t('EVSE.manual charging') : t(`EVSE.statusKey.${status}`);
      default:
        return t(`EVSE.statusKey.${status}`);
    }
  };
  const chargerStateDescription = () => {
    if (errorMessage) return errorMessage;

    switch (mode) {
      case 'error':
        return errorMessage;
      case 'fw-pending':
        return t('EVSE.firmware update check body');
      case 'fw-downloading':
        return t('EVSE.downloading firmware body');
      case 'fw-installing':
        return t('EVSE.firmware is updating body');
      case 'scheduled-waiting':
        return scheduleStartDayLabel
          ? t('EVSE.charging scheduled text with day', {
              day: scheduleStartDayLabel,
              startTime: formatScheduleTime(scheduleStartTime),
            })
          : t('EVSE.charging scheduled text', { startTime: formatScheduleTime(scheduleStartTime) });
      case 'evse-smartplug-stopped':
        return t('EVSE.evse smartplug temperature increased body');
      default:
        if (smartAmpStatus === 'active' && smartAmpLoadActive) {
          return t('EVSE.charging speed reduced due to load');
        }
        if (smartAmpStatus === 'active' && smartAmpTempActive) {
          return evseType === 'SmartAmp'
            ? t('EVSE.charging speed reduced due to temperature by smartamp')
            : t('EVSE.charging speed reduced due to temperature by smartplug');
        }
        if (smartAmpStatus === 'chargingLimited') {
          return t('EVSE.charging limited by smartamp');
        }
        return t(`EVSE.statusDescKey.${status}`);
    }
  };
  const storageKey = 'staging-push-notification-warning';
  const buildVersion = packageJson.version;

  useEffect(() => {
    if (site.commissioning_status !== 'PENDING' || isLoaded || !commissioningStartedAt) return;

    const elapsed = Date.now() - commissioningStartedAt;
    const remaining = COMMISSIONING_TIMEOUT_MS - elapsed;
    if (remaining <= 0) {
      setCommissioningNow(Date.now());
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCommissioningNow(Date.now());
    }, remaining);

    return () => window.clearTimeout(timeoutId);
  }, [site.commissioning_status, isLoaded, commissioningStartedAt]);

  useEffect(() => {
    if (isFirmwareMode) {
      clearSeenFirmwareToasts();
    }
  }, [isFirmwareMode]);

  useEffect(() => {
    if (cloudFirmwareStatus === 'Success') {
      if (!hasSeenFirmwareToast('success', firmwareVersion)) {
        toast({
          render: ({ onClose }) => (
            <FirmwareUpdatedToast
              title={t('EVSE.firmware updated success title')}
              description={t('EVSE.firmware updated success desc', {
                version: firmwareVersion?.replace(/^v/i, '') ?? '',
              })}
              onClose={onClose}
            />
          ),
        });
        markFirmwareToastSeen('success', firmwareVersion);
      }
      dispatch(setFirmwareUpdateStatus({ status: null, cloudStatus: null }));
    } else if (cloudFirmwareStatus === 'Failed' || cloudFirmwareStatus === 'FailedChargerNotConnected') {
      if (!hasSeenFirmwareToast('failure', firmwareVersion)) {
        toast({
          duration: null,
          render: ({ onClose }) => (
            <FirmwareUpdatedToast
              variant="failure"
              title={t('EVSE.firmware update failed title')}
              description={t('EVSE.firmware update failed desc')}
              onClose={onClose}
            />
          ),
        });
        markFirmwareToastSeen('failure', firmwareVersion);
      }
      dispatch(setFirmwareUpdateStatus({ status: null, cloudStatus: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudFirmwareStatus]);

  // Timeout: if firmware update states remain active for too long, clear them so
  // the normal dashboard is shown. Firmware states are BE/SSE-driven.
  useEffect(() => {
    if (!isFirmwareMode) return;

    const timerId = window.setTimeout(() => {
      dispatch(setFirmwareUpdateStatus({ status: null, cloudStatus: null }));
    }, FIRMWARE_UPDATE_CHECK_TIMEOUT_MS);

    return () => window.clearTimeout(timerId);
  }, [isFirmwareMode, dispatch]);

  useEffect(() => {
    if (isLoaded) return;

    if (!hasEvChargers) {
      dispatch(setIsLoaded(true));
      navigate('../device-not-found', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, hasEvChargers, navigate, dispatch]);

  // Track cable plug in/out via status transitions.
  useEffect(() => {
    if (!isLoaded || !status) return;

    const prev = previousStatus.current;
    if (prev && prev !== status) {
      // Plug in: from Available to Preparing.
      if (prev === 'Available' && status === 'Preparing' && !isTimedScheduleEnabled) {
        trackCablePluggedInWithoutSchedule();
        trackDynatraceCablePluggedInWithoutSchedule();
      }
      // Plug out: from Charging/SuspendedEVSE/Finishing to Available/SuspendedEV.
      if (['Charging', 'SuspendedEVSE', 'Finishing'].includes(prev) && ['Available', 'SuspendedEV'].includes(status)) {
        trackCablePluggedOut();
        trackDynatraceCablePluggedOut();
      }
    }
    previousStatus.current = status;
  }, [status, isLoaded, isTimedScheduleEnabled]);

  useEffect(() => {
    const update = () => {
      if (window.innerHeight <= 700) setAnimationWidth('min(100%, calc(35vh * 1.121))');
      else setAnimationWidth('min(100%, calc(45vh * 1.121))');
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Fetch schedules if not fetched yet.
  useEffect(() => {
    if (siteDeviceId && !areSchedulesLoaded) {
      dispatch(fetchEvChargerSchedules(siteDeviceId));
    }
  }, [areSchedulesLoaded, dispatch, siteDeviceId]);

  // Show a toast notification if push notifications are unavailable in the staging environment and production builds.
  useEffect(() => {
    if (!IS_PRODUCTION_DATA && IS_PRODUCTION && localStorage.getItem(storageKey) !== buildVersion) {
      toast({
        title: t('Notifications.push notifications unavailable.title'),
        description: t('Notifications.push notifications unavailable.description'),
        status: 'info',
        duration: null,
        isClosable: true,
        position: 'bottom',
      });

      localStorage.setItem(storageKey, buildVersion);
    }
  }, [toast, buildVersion, storageKey]);

  return (
    <SlidingAnimationPageBase
      includeTopNavHamButton
      includeSchneiderHamButton
      includeBottomNav
      title={<HeaderIcon w={'100%'} color={iconColor} />}
    >
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        px={4}
        h="100%"
        fontFamily="-apple-system, 'SF Pro Text', BlinkMacSystemFont, sans-serif"
      >
        <SkeletonText mt={2} isLoaded={isSkeletonBypassed} spacing={5}>
          {(() => {
            const titleText = isCommissioning
              ? t('EVSE.connecting charger title')
              : chargerStateTitle() || t('EVSE.unavailable');
            return (
              <Heading lineHeight="100%" fontSize={28} fontWeight="normal" data-testid="charging-status-title">
                {/* Fade between titles whenever the status / fault title changes.
                    Rendered inline (span) so it's valid inside the Heading (h*). */}
                <FadingText as="span" display="inline-block" textKey={titleText}>
                  {titleText}
                </FadingText>
              </Heading>
            );
          })()}

          {!NO_DESCRIPTION_STATUSES.includes(status) && (
            <Text as="div" data-testid="charging-status-description">
              {/* Fade between subtitles whenever the description / fault message changes. */}
              <FadingText
                textKey={
                  isCommissioning
                    ? 'commissioning'
                    : errorMessage
                    ? `error:${vendorErrorCode ?? errorMessage}`
                    : chargerStateDescription() || 'unknown'
                }
              >
                {isCommissioning ? (
                  t('EVSE.connecting charger description')
                ) : errorMessage ? (
                  <Popover>
                    <PopoverTrigger>
                      <Text as="span" textDecoration="underline" cursor="pointer">
                        {t('Common.show more details')}
                      </Text>
                    </PopoverTrigger>
                    <PopoverContent left={4} w={'auto'} maxW={'92vw'}>
                      <PopoverArrow />
                      <PopoverBody borderRadius={14} boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px" fontSize={14}>
                        <ReactMarkdown
                          components={{
                            p({ children }: { children?: React.ReactNode }) {
                              return (
                                <Text my={2} fontSize={14}>
                                  {children}
                                </Text>
                              );
                            },
                            ol({ children }: { children?: React.ReactNode }) {
                              return (
                                <Box as={'ol'} pl={4} fontSize={14}>
                                  {children}
                                </Box>
                              );
                            },
                          }}
                        >
                          {descriptionDetails}
                        </ReactMarkdown>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                ) : hasSmartAmpDetails || status === 'SuspendedEV' ? (
                  <>
                    {chargerStateDescription()}
                    <Popover>
                      <PopoverTrigger>
                        <Text as="span" display="block" mt={1} textDecoration="underline" cursor="pointer">
                          {t('Common.show more details')}
                        </Text>
                      </PopoverTrigger>
                      <PopoverContent w={'auto'} maxW={'92vw'}>
                        <PopoverArrow />
                        <PopoverBody borderRadius={14} boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px" fontSize={14}>
                          <ReactMarkdown
                            components={{
                              p({ children }: { children?: React.ReactNode }) {
                                return (
                                  <Text my={2} fontSize={14}>
                                    {children}
                                  </Text>
                                );
                              },
                              ol({ children }: { children?: React.ReactNode }) {
                                return (
                                  <Box as={'ol'} pl={4} fontSize={14}>
                                    {children}
                                  </Box>
                                );
                              },
                            }}
                          >
                            {descriptionDetails}
                          </ReactMarkdown>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </>
                ) : (
                  chargerStateDescription() ||
                  t('Common.something went wrong') + ' ' + t('Common.if this persists contact support')
                )}
              </FadingText>
            </Text>
          )}
        </SkeletonText>
        <Center flexDirection="column" textAlign="center" flex={1} overflowY="auto">
          <Box mt={2} mx="auto" style={{ width: animationWidth, aspectRatio: '325 / 290' }}>
            <Skeleton isLoaded={isSkeletonBypassed} w="100%" h="100%">
              <StatusAnimation
                status={animationStatus}
                isFirmwareMode={isFirmwareMode}
                isFirmwareInstalling={mode === 'fw-installing'}
                isReducedSpeed={
                  (smartAmpStatus === 'active' && (smartAmpLoadActive || smartAmpTempActive)) ||
                  smartAmpStatus === 'chargingLimited'
                }
                isChargingStopped={['evse-smartamp-stopped', 'evse-smartplug-stopped'].includes(mode)}
                isTemperatureIncreased={smartAmpStatus !== 'inactive' && smartAmpTempActive}
                evseType={evseType}
              />
            </Skeleton>
          </Box>
        </Center>

        <Box px={2} pb={`calc(${BOTTOM_NAV_HEIGHT} / 2)`} flexShrink={0} textAlign="center">
          {isCommissioning ? (
            <>
              <MultipurposeBanner isWaitingForActivation={true} />
              <ContactSupportButton path="../../account/support?direction=back&backURL=../../home/device-status" />
            </>
          ) : (
            <>
              <MultipurposeBanner />
              <ChargingControl />
            </>
          )}
        </Box>
      </Box>
    </SlidingAnimationPageBase>
  );
};
